/**
 * Utility to translate gRPC/Connect errors into user-friendly messages
 */

import { ConnectError } from '@connectrpc/connect';
import { Code } from '@connectrpc/connect';

/**
 * Maps gRPC error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<number, string> = {
    [Code.Unauthenticated]: 'Invalid credentials. Please check your email and password.',
    [Code.PermissionDenied]: 'You do not have permission to perform this action.',
    [Code.NotFound]: 'The requested resource was not found.',
    [Code.AlreadyExists]: 'This resource already exists.',
    [Code.InvalidArgument]: 'Invalid input. Please check your data and try again.',
    [Code.FailedPrecondition]: 'This action cannot be completed at this time.',
    [Code.Aborted]: 'The operation was aborted. Please try again.',
    [Code.OutOfRange]: 'The value is out of acceptable range.',
    [Code.Unimplemented]: 'This feature is not yet available.',
    [Code.Internal]: 'An internal error occurred. Please try again later.',
    [Code.Unavailable]: 'Service is temporarily unavailable. Please try again.',
    [Code.DataLoss]: 'Data loss detected. Please contact support.',
    [Code.DeadlineExceeded]: 'Request timed out. Please try again.',
    [Code.ResourceExhausted]: 'Resource limit exceeded. Please try again later.',
    [Code.Canceled]: 'The operation was canceled.',
};

/**
 * Context-specific error messages for different operations
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
    'invalid_credentials': 'Invalid email or password. Please try again.',
    'account_locked': 'Your account has been locked. Please contact support.',
    'account_disabled': 'Your account has been disabled. Please contact support.',
    'email_not_verified': 'Please verify your email address before logging in.',
    'mfa_required': '2FA_REQUIRED', // Special case - triggers navigation
    'invalid_token': 'Your session has expired. Please log in again.',
    'token_expired': 'Your session has expired. Please log in again.',
};

/**
 * Context-specific error messages for different operations
 */
const CONTEXT_ERROR_MESSAGES: Record<string, Record<number, string>> = {
    'fraud_report': {
        [Code.InvalidArgument]: 'Please check your report details and try again.',
        [Code.PermissionDenied]: 'You do not have permission to submit fraud reports.',
        [Code.AlreadyExists]: 'A report for this transaction already exists.',
        [Code.NotFound]: 'The fraud report could not be found.',
    },
    'dispute': {
        [Code.InvalidArgument]: 'Please check your dispute details and try again.',
        [Code.PermissionDenied]: 'You do not have permission to file disputes.',
        [Code.AlreadyExists]: 'A dispute for this transaction already exists.',
        [Code.NotFound]: 'The dispute could not be found.',
        [Code.FailedPrecondition]: 'This transaction cannot be disputed at this time.',
    },
    'payment': {
        [Code.InvalidArgument]: 'Please check your payment details and try again.',
        [Code.FailedPrecondition]: 'Payment cannot be processed at this time. Please check your payment method.',
        [Code.ResourceExhausted]: 'Payment limit exceeded. Please contact support.',
        [Code.NotFound]: 'The payment method could not be found.',
    },
    'subscription': {
        [Code.InvalidArgument]: 'Please select a valid subscription plan.',
        [Code.AlreadyExists]: 'You already have an active subscription.',
        [Code.FailedPrecondition]: 'Please complete your payment method setup first.',
        [Code.ResourceExhausted]: 'Subscription limit reached. Please contact support.',
    },
    'trust_score': {
        [Code.InvalidArgument]: 'Please provide valid information for the trust score scan.',
        [Code.ResourceExhausted]: 'You have reached your scan limit. Please purchase more credits.',
        [Code.NotFound]: 'The trust score report could not be found.',
    },
    'evidence': {
        [Code.InvalidArgument]: 'Please check your evidence details and try again.',
        [Code.PermissionDenied]: 'You do not have permission to access this evidence.',
        [Code.NotFound]: 'The evidence could not be found.',
        [Code.ResourceExhausted]: 'Storage limit exceeded. Please contact support.',
    },
    'ai_agent': {
        [Code.InvalidArgument]: 'Please check your AI agent configuration and try again.',
        [Code.PermissionDenied]: 'You do not have permission to register AI agents.',
        [Code.AlreadyExists]: 'An AI agent with this name already exists.',
        [Code.ResourceExhausted]: 'AI agent limit reached. Please upgrade your plan.',
    },
    'user_management': {
        [Code.InvalidArgument]: 'Please check the user details and try again.',
        [Code.PermissionDenied]: 'You do not have permission to manage users.',
        [Code.AlreadyExists]: 'A user with this email already exists.',
        [Code.NotFound]: 'The user could not be found.',
    },
};

/**
 * Common authentication error patterns
 */
export const AUTH_ERRORS = {
    NOT_AUTHENTICATED: 'Please log in to continue',
    SESSION_EXPIRED: 'Your session has expired. Please log in again',
    NOT_AUTHORIZED: 'You do not have permission to perform this action',
    INVALID_TOKEN: 'Your session is invalid. Please log in again',
} as const;

/**
 * Common feature not implemented messages
 */
export const FEATURE_NOT_AVAILABLE = {
    COMING_SOON: 'This feature is coming soon. Please contact support if you need immediate assistance.',
    UNDER_MAINTENANCE: 'This feature is temporarily unavailable for maintenance. Please try again later.',
} as const;

/**
 * Extracts a user-friendly error message from a gRPC/Connect error
 * @param error - The error object (ConnectError or generic Error)
 * @param context - Optional context for more specific error messages (e.g., 'login', 'fraud_report', 'payment')
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: unknown, context?: string): string {
    // Handle ConnectError (from @connectrpc/connect)
    if (error instanceof ConnectError) {
        // Check for context-specific error messages first
        if (context && context in CONTEXT_ERROR_MESSAGES) {
            const contextMessages = CONTEXT_ERROR_MESSAGES[context];
            if (error.code in contextMessages) {
                return contextMessages[error.code];
            }
        }

        // Check if the error message contains a known auth error key
        const lowerMessage = error.message.toLowerCase();
        for (const [key, message] of Object.entries(AUTH_ERROR_MESSAGES)) {
            if (lowerMessage.includes(key)) {
                return message;
            }
        }

        // Check for specific error codes
        if (error.code in ERROR_MESSAGES) {
            return ERROR_MESSAGES[error.code];
        }

        // Fallback to code-based message
        return ERROR_MESSAGES[error.code] || 'An unexpected error occurred. Please try again.';
    }

    // Handle standard Error objects
    if (error instanceof Error) {
        // Check for special cases in the message
        if (error.message === '2FA_REQUIRED') {
            return error.message; // Pass through for navigation logic
        }

        // Check if it's a known auth error
        const lowerMessage = error.message.toLowerCase();
        for (const [key, message] of Object.entries(AUTH_ERROR_MESSAGES)) {
            if (lowerMessage.includes(key)) {
                return message;
            }
        }

        // Return the error message if it looks user-friendly (not too technical)
        if (error.message && !error.message.includes('Error:') && error.message.length < 200) {
            return error.message;
        }
    }

    // Fallback for unknown errors
    return 'An unexpected error occurred. Please try again.';
}

/**
 * Checks if an error requires special handling (e.g., navigation)
 */
function requiresSpecialHandling(error: unknown): string | null {
    if (error instanceof ConnectError || error instanceof Error) {
        const message = getErrorMessage(error);
        if (message === '2FA_REQUIRED') {
            return '2FA_REQUIRED';
        }
    }
    return null;
}
